import React from 'react';

import { ACHCalculator } from 'src/components-v3/ACHCalculator/ACHCalculator';
import { LendScoreCalculator } from 'src/components-v3/LendScoreCalculator/LendScoreCalculator';
import { MortgageCalculator } from 'src/components-v3/MortgageCalculator/MortgageCalculator';
import type {
  CalculatorType,
  SectionCalculatorEntry,
} from 'src/types/contentful';

type CalculatorProps = {
  fields: SectionCalculatorEntry['fields'];
  sys: SectionCalculatorEntry['sys'];
};

// The calculators keep their own state, layout, and animations, so the entry's
// `calculatorType` only decides which one renders. The keys have to match the
// values the Contentful field allows exactly: a value with no calculator here
// renders nothing.
//
// Built when it's called, not when this module loads. Each calculator leads
// back to this module through RichTextSwitch and CmsComponentSwitch, so when a
// calculator is imported before this module, this module runs before that
// calculator is defined.
//
// Typed as `ElementType` rather than `ComponentType<CalculatorProps>`: each
// calculator declares the props of its own content type, and a component's
// `propTypes` are checked against the props it is assigned to, which rejects
// the narrower per-calculator types.
export const getCalculatorComponents = (): Record<
  CalculatorType,
  React.ElementType
> => {
  return {
    ACH: ACHCalculator,
    Mortgage: MortgageCalculator,
    LendScore: LendScoreCalculator,
  };
};

const isCalculatorType = (value: unknown): value is CalculatorType => {
  // An own-property check, so inherited keys such as `constructor` are not
  // mistaken for a calculator.
  return (
    typeof value === 'string' &&
    Object.prototype.hasOwnProperty.call(getCalculatorComponents(), value)
  );
};

export const CalculatorSwitch: React.FC<CalculatorProps> = ({
  fields,
  sys,
}) => {
  const calculatorType: unknown = fields?.calculatorType;

  if (!isCalculatorType(calculatorType)) {
    return null;
  }

  const Calculator = getCalculatorComponents()[calculatorType];

  return <Calculator fields={fields} sys={sys} />;
};
